import { useEffect, useRef, useState } from 'react';

const DEFAULT_LONG_PRESS_TIME_MS = 400;
const DEFAULT_DELAY_MS = 100;

interface IProps {
  longPressEnabled?: boolean;
  onContextMenu: (e: React.MouseEvent | React.TouchEvent) => void;
  delayMs?: number; // how long until we consider it a long press
  pressTimeMs?: number; // how long to press before triggering the context menu
}

/**
 * Takes an `onContextMenu` callback and returns a `isLongPressing` state & a set of event handlers
 * for touch screens that will trigger the provided `onContextMenu` after long-pressing. The original
 * `onContextMenu` will also be provided and called on the traditional right click event.
 *
 * Designed to be prop spread onto a component that triggers a context menu.
 * e.g. <Table.Row onClick={_onClick} className={isLongPressing ? 'select-none' : ''} {...onContextMenuHandlers} />
 */
export const useOnContextMenu = ({
  onContextMenu,
  pressTimeMs = DEFAULT_LONG_PRESS_TIME_MS,
  delayMs = DEFAULT_DELAY_MS,
  longPressEnabled = false,
}: IProps) => {
  const [isLongPressing, setIsLongPressing] = useState(false);

  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startDelayRef = useRef<NodeJS.Timeout | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    startDelayRef.current = setTimeout(() => {
      setIsLongPressing(true);

      longPressTimerRef.current = setTimeout(() => {
        onContextMenu(e);
      }, pressTimeMs);
    }, delayMs);
  };

  const reset = () => {
    setIsLongPressing(false);

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (startDelayRef.current) {
      clearTimeout(startDelayRef.current);
      startDelayRef.current = null;
    }
  };

  useEffect(() => {
    // clear any still-running timers on unmount
    return () => {
      reset();
    };
  }, []);

  // opt-out
  if (!longPressEnabled) {
    return {
      isLongPressing,
      onContextMenuHandlers: {
        onContextMenu,
      },
    };
  }

  return {
    isLongPressing,
    onContextMenuHandlers: {
      onContextMenu,
      onTouchStart: handleTouchStart,
      onTouchEnd: reset,
      onTouchMove: reset,
      onTouchCancel: reset,
    },
  };
};
